import { privateAxios, serviceAxios } from "../axios-instances.ts";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {useErrorPopUp, usePopUpStore} from "../../warnings/store/PopUpStore.js";
import mem from "mem";
import { axiosInterceptor } from "../axios-client.js";

export function useUserServiceApi() {
    const queryClient = useQueryClient();
    const [accessToken, setAccessToken] = useState<string | null>(sessionStorage.getItem("accessToken") || null);
    const [role, setRole] = useState<string | null>(sessionStorage.getItem("userType") || null);

    //success redirects
    const navigate = useNavigate();

    const refreshTokenPromise = async (): Promise<string> => {
        const res = await serviceAxios.put("/auth/refresh");
        return res.data.accessToken;
    }

    const refreshToken = useMutation({
        mutationFn: refreshTokenPromise,
        onSuccess: (newToken: string) => {
            sessionStorage.setItem("accessToken", newToken);
            setAccessToken(newToken);
            axiosInterceptor();
        },
        onError: (err: any) => {
            console.error("Refresh token failed:", err);
            switch (err.status) {
                case 401:
                    return navigate({to: "/login"})
                case 404:
                    useErrorPopUp.getState().openPopUp("Помилка:", "Користувача не знайдено");
                    return navigate({to: "/login"})
                case 500:
                    return useErrorPopUp.getState().openPopUp("Помилка:", "Помилка серверу");
                default:
                    return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
            }
        },
    });

    const maxAge = 10000;
    const memorizedRefreshToken = mem(refreshTokenPromise, {maxAge})

    const registryUser = useMutation({
        mutationFn: async (credentials: { userType: string, email: string, password: string }) => {
            await serviceAxios.post("/auth/sign-up", credentials);
        },
        onSuccess: () => {
            usePopUpStore.getState().openPopUp(
                "Дані записано",
                "Тепер перевірте вашу пошту та активуйте акаунт"
            );
        },
        onError: (err: any) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Некоректні дані");
                    case 409:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Акаунт з даною адресою вже існує");
                    case 500:
                        return useErrorPopUp.getState().openPopUp("Помилка:", "Помилка серверу");
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
            } else {
                console.error(err);
            }
        }
    })

    const activateLink = useMutation({
        mutationFn: async (link: string) => {
            const res = await privateAxios.patch("/auth/activate", { link });
            return res.data;
        },
        onSuccess: (res: {accessToken: string, userType: string}) => {
            if (!res.accessToken) {
                console.error("No token received, something went wrong!");
                return;
            }
            setAccessToken(res.accessToken);
            //axiosInterceptor();
            setRole(res.userType);
            sessionStorage.setItem("accessToken", res.accessToken);
            sessionStorage.setItem("userType", res.userType);
            usePopUpStore.getState().openPopUp(
                "Реєстрацію завершено!",
                "Пропонуємо заповнити власні дані"
            );
            navigate({to: `/user`});
        },
        onError: (err: any) => {
            if (axios.isAxiosError(err)) {
                console.error(err.response);
                const status = err.response?.status;
                switch (status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Посилання активації не знайдено");
                        break;
                    case 409:
                        useErrorPopUp.getState().openPopUp("Помилка", "Посилання активації вже було використано");
                        break;
                    case 410:
                        useErrorPopUp.getState().openPopUp("Помилка", "Посилання активації вже не дійсне");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка серверу");
                        break;
                    default:
                        useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
                navigate({to: "/login"});
            } else {
                console.error(err);
            }
        }
    });

    //handling access token with tanstack query mutation (if success -> store)
    //add the check for the existing access token
    const loginUser = useMutation({
        mutationFn: async (credentials: { email: string, password: string }) => {
            const res = await serviceAxios.post("/auth/sign-in", credentials);
            return res.data;
        },
        onSuccess: async (res: {accessToken: string, userType: string}) => {
            try{
                setAccessToken(res.accessToken);
                setRole(res.userType);
                sessionStorage.setItem("accessToken", res.accessToken);
                sessionStorage.setItem("userType", res.userType);
                await navigate({to: "/user"});
            } catch (error) {
                console.error(error);
            }
        },
        onError: (err: any) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Акаунт не акитвовано або не вірний вхід");
                        break;
                    case 409:
                        useErrorPopUp.getState().openPopUp("Помилка", "Посилання активації ще не було використано");
                        break;
                    case 410:
                        useErrorPopUp.getState().openPopUp("Помилка:", "Посилання активації вже не дійсне");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка:", "Помилка серверу");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
            } else {
                console.error(err);
            }
        }
    });

    const logoutUser = useMutation({
        mutationFn: async () => {
            await privateAxios.patch("/auth/logout", {})
        },
        onSuccess: () => {
            setAccessToken(null);
            setRole(null);
            sessionStorage.removeItem("accessToken");
            sessionStorage.removeItem("userType");
            sessionStorage.removeItem("type");
            queryClient.clear();
            navigate({to: "/login"})
        },
        onError: (err: any) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Некоректні дані");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка:", "Помилка серверу");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
            } else {
                console.error(err);
            }
        }
    })
    const changePassword = useMutation({
        mutationFn: async (email: string) => {
            await serviceAxios.post("/auth/change-password", {email});
        },
        onSuccess: () => {
            console.log("Password change link is sent")
            usePopUpStore.getState().openPopUp(
                "Лист для зміни паролю надіслано",
                "Перевірте свою пошту, аби змінити пароль"
            );
        },
        onError: (err: any) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Акаунт користувача не активовано");
                        break;
                    case 410:
                        useErrorPopUp.getState().openPopUp("Помилка", "Посилання зміни вже не дійсне");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка:", "Помилка серверу");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
            } else {
                console.error(err);
            }
        }
    })

    const setPasswordChange = useMutation({
        mutationFn: async (credentials: { link: string, password: string}) => {
            await privateAxios.patch("/auth/change-password", credentials);
        },
        onSuccess: () => {
            navigate({to: "/login"})
            usePopUpStore.getState().openPopUp(
                "Пароль успішно оновлено",
                "Дані вашого акаунту були успішно оновлені"
            )
        },
        onError: (err:any) => {
            if (axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Хибні дані");
                        break;
                    case 409:
                        useErrorPopUp.getState().openPopUp("Помилка", "Посилання зміни використано");
                        break;
                    case 410:
                        useErrorPopUp.getState().openPopUp("Помилка:", "Посилання зміни вже не дійсне");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка:", "Помилка серверу");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
                navigate({to: "/login"})
            } else {
                console.error(err);
            }
        }
    })

    const setUserType = useMutation({
        mutationFn: async (userType: string) => {
            const res = await privateAxios.put("/auth/user-type", {userType});
            return res.data;
        },
        onSuccess: (userType: string) => {
            setRole(userType);
            //navigate({to: "/user"})
            /*usePopUpStore.getState().openPopUp(
                "Вхід виконано",
                "Ласкаво просимо до особистого кабінету"
            )*/
        },
        onError: (err) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 409:
                       useErrorPopUp.getState().openPopUp("Помилка", "Тип акаунту вже обрано");
                       break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка серверу");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
                navigate({to: "/login"})
            } else {
                 console.error(err)
            }
        }
    })

    const adminGlobalLogin = useMutation({
        mutationFn: async (credentials: { login: string, password: string }) => {
            const res = await serviceAxios.post("/auth/admin/sign-in/global", credentials);
            return res.data;
        },
        onSuccess: (shortToken: string) => {
            usePopUpStore.getState().openPopUp(
                "Перевірку виконано",
                "Наразі просимо викорнати авторизацію як адміністратора для отримання відповідних прав"
            )
            navigate({
                to: "/admin/current-admin-login",
                search: {data: encodeURIComponent(JSON.stringify(shortToken))}
            })
        },
        onError: (err) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка запиту");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка серверу");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
                navigate({to: "/admin/global-login"})
            } else {
                console.error(err)
            }
        }

    })

    const adminCurrentLogin = useMutation({
        mutationFn: async (credentials: { login: string, password: string, token: string }) => {
            const res = await serviceAxios.post("/auth/admin/sign-in", credentials);
            return res.data;
        },
        onSuccess: (res: {accessToken: string, type: string}) => {
            usePopUpStore.getState().openPopUp(
                "Авторизацію пройдено",
                "Доступ до сторінки адміністратора дозволений"
            )
            setAccessToken(res.accessToken);
            setRole("admin");
            sessionStorage.setItem("accessToken", res.accessToken);
            sessionStorage.setItem("userType", "admin")
            sessionStorage.setItem("type", res.type)
            window.location.href = "/admin/home-page"
        },
        onError: (err) => {
            if(axios.isAxiosError(err)) {
                console.log(err.status)
                console.error(err.response)
                switch (err.status) {
                    case 400:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка запиту");
                        break;
                    case 403:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка токену");
                        break;
                    case 408:
                        useErrorPopUp.getState().openPopUp("Помилка", "Застарілий токен");
                        break;
                    case 500:
                        useErrorPopUp.getState().openPopUp("Помилка", "Помилка валідації токену");
                        break;
                    default:
                        return useErrorPopUp.getState().openPopUp("Помилка", "Невідома помилка");
                }
                navigate({to: "/admin/global-login"})
            } else {
                console.error(err)
            }
        }

    })

    return {
        refreshTokenPromise,
        refreshToken,
        memorizedRefreshToken,
        registryUser,
        activateLink,
        loginUser,
        logoutUser,
        changePassword,
        setPasswordChange,
        setUserType,
        accessToken,
        setAccessToken,
        role,
        setRole,
        adminGlobalLogin,
        adminCurrentLogin
    };
}